import styled, { createGlobalStyle } from 'styled-components';

import { PAGE_NAV } from '../theme/tokens';

/** Direction of travel for a navigation. `none` = first paint (fade only). */
export type NavDir = 'forward' | 'back' | 'none';
/** Whether a layer is the page arriving (`enter`) or the page leaving (`exit`). */
export type LayerRole = 'enter' | 'exit';

/**
 * The hand-written pages render into the config-driven layout's content "card".
 * `PageShell` neutralises that card's background/border by matching the card as
 * the *direct parent* of `.ai-bb-page-root`. The transition host now sits between
 * the card and the page, so we re-apply the same neutralisation one level up —
 * matched off the host class, so it only ever touches that single card slot and
 * never the surrounding chrome (left menu / top bar / Lumio panel).
 */
export const TransitionBackdropReset = createGlobalStyle`
  *:has(> .ai-bb-page-transition) {
    background: transparent !important;
    border: none !important;
  }
`;

/**
 * Holds the entering + (briefly) the leaving page during a navigation.
 *
 * `overflow-x: clip` hides the horizontal slide so the off-screen page can never
 * add a scrollbar. We use `clip` rather than `hidden` precisely because it leaves
 * the vertical axis `visible` (it does NOT become a scroll container) — so each
 * page keeps owning its own internal vertical scroll, exactly as before.
 */
export const TransitionHost = styled.div<{ $fill?: boolean; $clip?: boolean }>`
  position: relative;
  width: 100%;
  /* Routes fill the fixed-height page viewport ($fill, the default); in-page swaps
     (e.g. Settings list ↔ a section) size to their content instead. */
  ${({ $fill }) => ($fill === false ? '' : 'height: 100%;')}
  /* An in-page swap clips only while it slides ($clip false at rest): it sits INSIDE
     the page column, so a resting clip would cut what a view draws past its edges
     (a pinned header's pill reaches into the column gutter). */
  ${({ $clip }) => ($clip === false ? '' : 'overflow-x: clip;')}
`;

const enterOffset = (dir: NavDir): number => {
  if (dir === 'forward') return PAGE_NAV.OFFSET_PX; // arrives from the right
  if (dir === 'back') return -PAGE_NAV.OFFSET_PX; // arrives from the left
  return 0; // first paint — fade only, no slide
};

const exitOffset = (dir: NavDir): number => {
  if (dir === 'forward') return -PAGE_NAV.OFFSET_PX; // leaves to the left
  if (dir === 'back') return PAGE_NAV.OFFSET_PX; // leaves to the right
  return 0;
};

/**
 * A single page layer.
 *
 *  - `enter` is in normal flow (so it defines the host's height like the page
 *    always did) and animates from its travel-direction offset to rest. Its
 *    resting transform is `none` (not `translate(0)`) so it leaves behind no
 *    transform containing-block that would re-anchor the page's absolute header
 *    scrim.
 *  - `exit` is an absolute overlay (it has already handed height duties to the
 *    incoming layer) that the leaving page slides + fades out through, then it
 *    unmounts. The leaving page is a frozen React instance, so it shows its real
 *    finished pixels (filled gauges/bars, preserved scroll) — never a replay.
 *
 * Under `prefers-reduced-motion` the horizontal travel is dropped entirely and we
 * keep only a short crossfade.
 */
export const TransitionLayer = styled.div<{
  $role: LayerRole;
  $dir: NavDir;
  $entered: boolean;
  /** At rest after an in-page swap, or a route's page that holds a control over the app's chrome
   *  (`useAboveAppChrome`): drop `will-change`, whose stacking context would trap a pinned header's
   *  z-index under the page's header scrim, or a page's under the chrome. */
  $settled?: boolean;
}>`
  position: ${({ $role }) => ($role === 'exit' ? 'absolute' : 'static')};
  ${({ $role }) => ($role === 'exit' ? 'inset: 0;' : '')}
  ${({ $role }) => ($role === 'exit' ? 'pointer-events: none;' : '')}
  width: 100%;
  height: 100%;
  will-change: ${({ $settled }) => ($settled ? 'auto' : 'transform, opacity')};
  transition: transform ${PAGE_NAV.DURATION_MS}ms ${PAGE_NAV.EASE},
    opacity ${PAGE_NAV.DURATION_MS}ms ${PAGE_NAV.EASE};

  opacity: ${({ $role, $entered }) => ($role === 'exit' ? 0 : $entered ? 1 : 0)};
  transform: ${({ $role, $dir, $entered }) => {
    if ($role === 'exit') return `translate3d(${exitOffset($dir)}px, 0, 0)`;
    return $entered ? 'none' : `translate3d(${enterOffset($dir)}px, 0, 0)`;
  }};

  @media (prefers-reduced-motion: reduce) {
    transition: opacity ${Math.round(PAGE_NAV.DURATION_MS / 2)}ms ${PAGE_NAV.EASE};
    transform: none;
  }
`;
